import { Helmet } from "react-helmet-async";
import { Link, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MapPin, Phone, Check } from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import { fadeUp, stagger } from "@/lib/motion";
import { areas } from "@/lib/areas";

const SITE_URL = "https://psyloveyourself.com";

export default function LocalArea() {
  const slug = useLocation().pathname.replace(/^\/+|\/+$/g, "");
  const area = areas.find((a) => a.slug === slug);
  if (!area) return <Navigate to="/" replace />;

  const pageUrl = `${SITE_URL}/${area.slug}`;

  const schema = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "MedicalBusiness"],
    name: `${area.metaTitle.split("|")[0].trim()} · Άννα Παπαχρονόπουλου`,
    url: pageUrl,
    telephone: "+30-697-8042360",
    email: "anniepapach@gmail.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Ρήγα Φεραίου 119",
      addressLocality: "Πάτρα",
      addressRegion: "Αχαΐα",
      postalCode: "26221",
      addressCountry: "GR",
    },
    geo: { "@type": "GeoCoordinates", latitude: 38.246639, longitude: 21.734573 },
    medicalSpecialty: "Psychiatric",
    priceRange: "€€",
    inLanguage: "el-GR",
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: area.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Αρχική", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: area.badge, item: pageUrl },
    ],
  };

  return (
    <>
      <Helmet>
        <html lang="el-GR" />
        <title>{area.metaTitle}</title>
        <meta name="description" content={area.metaDescription} />
        <meta name="keywords" content={area.keywords} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={area.metaTitle.split("|")[0].trim()} />
        <meta property="og:description" content={area.metaDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:locale" content="el_GR" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbs)}</script>
      </Helmet>

      <Header />

      <main className="bg-background pt-32 pb-24" data-testid={`area-page-${area.slug}`}>
        <div className="max-w-[1200px] mx-auto px-6 sm:px-12 lg:px-20">
          {/* Hero */}
          <motion.div {...fadeUp}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />
              Αρχική
            </Link>
            <p className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border/60 text-[11px] tracking-[0.22em] uppercase font-body font-medium text-foreground/70 mb-6">
              <MapPin size={14} strokeWidth={1.5} className="text-primary" />
              {area.badge}
            </p>
            <h1 className="font-heading font-medium tracking-tight text-foreground text-4xl sm:text-5xl lg:text-[72px] leading-[1.04]">
              {area.h1a} <span className="italic font-light">{area.h1b}</span>
              <br />
              <span className="text-foreground/85 italic font-light">{area.sub}</span>
            </h1>
            <p className="mt-7 font-body font-light text-lg sm:text-xl text-foreground/75 leading-relaxed max-w-[680px]">
              {area.intro}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link
                to="/rantevou"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-foreground text-background px-8 py-4 font-body text-base font-medium hover:-translate-y-0.5 transition-all duration-300"
                data-testid="area-hero-cta"
              >
                Κλείσε Ραντεβού Online
                <ArrowRight size={18} strokeWidth={1.5} />
              </Link>
              <a
                href="tel:+306978042360"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-transparent px-8 py-4 font-body text-base font-medium text-foreground hover:bg-card transition-all"
              >
                <Phone size={16} strokeWidth={1.5} />
                697 8042 360
              </a>
            </div>
          </motion.div>

          {/* Points */}
          <motion.div {...stagger(0.08)} className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {area.points.map((p) => (
              <motion.div
                key={p.title}
                variants={{
                  initial: { opacity: 0, y: 24 },
                  whileInView: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
                  },
                }}
                className="bg-card border border-border/50 rounded-3xl p-7 sm:p-8"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-5">
                  <Check size={18} strokeWidth={2} />
                </div>
                <h2 className="font-heading text-2xl text-foreground tracking-tight">{p.title}</h2>
                <p className="mt-3 font-body font-light text-foreground/70 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* FAQ */}
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }} className="mt-20">
            <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-4">
              Συχνές ερωτήσεις
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight">
              Ό,τι θες να ξέρεις
            </h2>
            <div className="mt-8 space-y-4">
              {area.faqs.map((f, i) => (
                <div
                  key={f.q}
                  className="bg-card border border-border/50 rounded-2xl p-6 sm:p-7"
                  data-testid={`area-faq-${i + 1}`}
                >
                  <h3 className="font-heading text-xl text-foreground tracking-tight">{f.q}</h3>
                  <p className="mt-2.5 font-body font-light text-foreground/70 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Cross links */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-16 flex flex-wrap gap-3"
          >
            <Link
              to="/psixotherapeia-patra"
              className="inline-flex items-center gap-2 text-sm font-body text-foreground/75 hover:text-foreground bg-card border border-border/60 rounded-full px-5 py-2.5 transition-colors"
            >
              Ψυχοθεραπεία στην Πάτρα
              <ArrowRight size={13} strokeWidth={1.5} />
            </Link>
            {areas
              .filter((a) => a.slug !== area.slug)
              .map((a) => (
                <Link
                  key={a.slug}
                  to={`/${a.slug}`}
                  className="inline-flex items-center gap-2 text-sm font-body text-foreground/75 hover:text-foreground bg-card border border-border/60 rounded-full px-5 py-2.5 transition-colors"
                >
                  {a.badge}
                  <ArrowRight size={13} strokeWidth={1.5} />
                </Link>
              ))}
          </motion.div>

          {/* Final CTA */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.25 }}
            className="mt-16 p-10 sm:p-14 bg-foreground text-background rounded-3xl"
          >
            <h2 className="font-heading text-3xl sm:text-5xl tracking-tight leading-[1.05]">
              Κάνε το πρώτο βήμα
              <br />
              <span className="italic font-light">σήμερα.</span>
            </h2>
            <p className="mt-5 font-body font-light text-background/85 leading-relaxed max-w-[520px]">
              Διάλεξε ημέρα και ώρα online — δια ζώσης στην Πάτρα ή μέσω
              βιντεοκλήσης. Χωρίς δέσμευση, με πλήρη εμπιστευτικότητα.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/rantevou"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-background text-foreground px-7 py-3.5 font-body text-sm font-medium hover:-translate-y-0.5 transition-all"
                data-testid="area-final-cta"
              >
                Κλείσε Ραντεβού Online
                <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <a
                href="tel:+306978042360"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-background/30 px-7 py-3.5 font-body text-sm font-medium text-background hover:bg-background/10 transition-all"
              >
                <Phone size={14} strokeWidth={1.5} />
                697 8042 360
              </a>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </>
  );
}
